/**
 * GH#914 — recover an open session whose chunks a deploy removed.
 *
 * `deploy.yml` wipes `client/dist` when it swaps a build in, so a tab that was
 * already open holds an index.html pointing at hashed chunks the server no
 * longer has. The next lazy route import throws and the app dead-ends
 * (Sentry PSI-FOAM-CASEBUILDER-E). Vite raises `vite:preloadError` for this.
 *
 * A reload fixes it because the working build IS on the server. The risk is the
 * reload loop: if the import fails for a reason a reload cannot cure, reloading
 * on every failure traps the user worse than the original error did. So we
 * reload at most once per session and otherwise let the error through to the
 * ErrorBoundary and Sentry.
 *
 * The policy is a pure function over an injected storage so it can be tested
 * without a real page reload.
 */

export const STALE_PRELOAD_RELOAD_KEY = 'psi-foam:stale-preload-reloaded';

/** The slice of Storage this module needs — keeps tests free of a real DOM. */
export interface MarkerStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * True when we should reload to pick up the current build.
 *
 * Storage access is wrapped because Safari private mode throws on both reads
 * and writes. If we cannot record the attempt we cannot prove we have not
 * already made one, and an unprovable attempt is exactly the case that loops —
 * so the safe answer there is "do not reload".
 */
export function shouldReloadForStalePreload(storage: MarkerStorage): boolean {
  try {
    if (storage.getItem(STALE_PRELOAD_RELOAD_KEY)) return false;
    storage.setItem(STALE_PRELOAD_RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/**
 * Called once the app has mounted successfully: the recovery worked, so a
 * later deploy should be free to reload again rather than being treated as a
 * second failure of the same event.
 */
export function clearStalePreloadMarker(storage: MarkerStorage): void {
  try {
    storage.removeItem(STALE_PRELOAD_RELOAD_KEY);
  } catch {
    // Nothing to do — a storage that cannot clear also cannot have recorded.
  }
}

/**
 * Why a recovery stood aside. The two are NOT the same event (#954):
 *
 *   already-reloaded     the user got their one reload; the second failure is
 *                        the documented trade-off working as designed
 *   storage-unavailable  storage threw, so NO reload ever happened — Safari
 *                        private mode, where the user gets the pre-#914
 *                        experience and nothing said so
 *
 * The first is a decision. The second is an unmeasured residue, and until they
 * are distinguishable in the data #954 can only be argued about, not sized.
 */
export type StalePreloadSkipReason = 'already-reloaded' | 'storage-unavailable';

export interface StalePreloadOptions {
  target: Window;
  storage: MarkerStorage;
  reload: () => void;
  /** Optional; telemetry only. Never affects whether a reload happens. */
  onSkip?: (reason: StalePreloadSkipReason) => void;
}

/**
 * Did the reload actually get recorded? One read, no write — the decision has
 * already been made by the time this is asked, so re-probing must not add a
 * second side effect to the path it is describing.
 *
 * The MARKER, not readability, is what separates the two cases. A storage that
 * reads but cannot write — quota exceeded, or a read-only implementation —
 * looks perfectly readable while having recorded nothing, and no reload
 * happened. Classifying that as `already-reloaded` would undercount exactly the
 * residue this telemetry exists to size.
 */
function reloadWasRecorded(storage: MarkerStorage): boolean {
  try {
    return storage.getItem(STALE_PRELOAD_RELOAD_KEY) !== null;
  } catch {
    return false;
  }
}

/** Wire the `vite:preloadError` handler. Returns nothing; registration only. */
export function registerStalePreloadRecovery(options: StalePreloadOptions): void {
  const { target, storage, reload, onSkip } = options;
  target.addEventListener('vite:preloadError', (event: Event) => {
    if (!shouldReloadForStalePreload(storage)) {
      // #954: name the case before standing aside. The marker is the evidence
      // a reload happened — its ABSENCE means none did, whether storage threw
      // on read, on write, or silently accepted neither.
      try {
        onSkip?.(reloadWasRecorded(storage) ? 'already-reloaded' : 'storage-unavailable');
      } catch {
        // Telemetry must never break the recovery path it is describing.
      }
      // Let Vite's default run so the error still reaches the boundary/Sentry.
      return;
    }
    // We are handling it; suppress the default unhandled-rejection path.
    event.preventDefault?.();
    reload();
  });
}
